import React, { useState } from 'react';
import logo from './asset/logo.png'
import { Cookie, Settings, Shield, Info, Bell, Lock, Globe, Mail, Menu, X, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const CookiePolicy = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    functional: true,
    analytics: false
  });

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const togglePreference = (type) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Navbar */}
      <nav className="bg-black/80 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center gap-2">
               <img
                   src={logo} // replace with your image path
                   alt="Description"
                   width="100px"
                 />
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-gray-700 hover:text-indigo-600 transition-colors font-medium">
                Home
              </a>
             
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3 border-t border-gray-200">
              <a href="/" className="block text-gray-700 hover:text-indigo-600 transition-colors font-medium">
                Home
              </a>
            
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 opacity-90"></div>
        <div className="relative py-16 px-4 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Cookie className="w-5 h-5 animate-bounce" />
              <span className="text-sm font-semibold">Your Privacy Matters</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Cookie Policy
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Transparent information about how we use cookies to enhance your experience
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-white/80">
              <Bell className="w-4 h-4" />
              <span>Last Updated: January 1, 2025</span>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
      </div>

      {/* Cookie Preference Center */}
      <div className="max-w-4xl mx-auto px-4 -mt-8 mb-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-6 h-6 text-indigo-600" />
            Cookie Preferences
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-semibold text-gray-900">Essential Cookies</p>
                  <p className="text-sm text-gray-600">Required for site functionality</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-green-600 font-medium">Always Active</span>
                <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-semibold text-gray-900">Functional Cookies</p>
                  <p className="text-sm text-gray-600">Remember preferences</p>
                </div>
              </div>
              <button
                onClick={() => togglePreference('functional')}
                className={`w-12 h-6 rounded-full transition-colors duration-300 flex items-center ${
                  cookiePreferences.functional ? 'bg-blue-500 justify-end' : 'bg-gray-300 justify-start'
                } px-1`}
              >
                <div className="w-4 h-4 bg-white rounded-full"></div>
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <Info className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-semibold text-gray-900">Analytics Cookies</p>
                  <p className="text-sm text-gray-600">Help us improve</p>
                </div>
              </div>
              <button
                onClick={() => togglePreference('analytics')}
                className={`w-12 h-6 rounded-full transition-colors duration-300 flex items-center ${
                  cookiePreferences.analytics ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
                } px-1`}
              >
                <div className="w-4 h-4 bg-white rounded-full"></div>
              </button>
            </div>
          </div>
          <button className="w-full mt-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300">
            Save Preferences
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        {/* What Are Cookies */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Cookie className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">What Are Cookies?</h2>
              <p className="text-gray-700 leading-relaxed">
                Cookies are small text files that are stored on your device when you visit 
                websites. They help the website remember your preferences and improve your 
                browsing experience.
              </p>
            </div>
          </div>
        </div>

        {/* Cookie Types */}
        <div className="space-y-4 mb-6">
          {/* Essential Cookies */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <button
              onClick={() => toggleSection(1)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Essential Cookies</h2>
              </div>
              {expandedSection === 1 ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 1 && (
              <div className="px-6 pb-6 border-t border-gray-100 pt-6 animate-fadeIn">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Required for Operation</span>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-green-700">
                      <span className="text-green-500 mt-1">•</span>
                      <span>User authentication and session management</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-700">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Security and fraud prevention</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-700">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Load balancing and performance</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-700">
                      <span className="text-green-500 mt-1">•</span>
                      <span>Cannot be disabled without affecting functionality</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Functional Cookies */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <button
              onClick={() => toggleSection(2)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Functional Cookies</h2>
              </div>
              {expandedSection === 2 ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 2 && (
              <div className="px-6 pb-6 border-t border-gray-100 pt-6 animate-fadeIn">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-800">Enhance User Experience</span>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-blue-700">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Remembering user preferences and settings</span>
                    </li>
                    <li className="flex items-start gap-2 text-blue-700">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Language and display preferences</span>
                    </li>
                    <li className="flex items-start gap-2 text-blue-700">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Document auto-save functionality</span>
                    </li>
                    <li className="flex items-start gap-2 text-blue-700">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Customized interface settings</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Analytics Cookies */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <button
              onClick={() => toggleSection(3)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Info className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Analytics Cookies</h2>
              </div>
              {expandedSection === 3 ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 3 && (
              <div className="px-6 pb-6 border-t border-gray-100 pt-6 animate-fadeIn">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-purple-800">Service Improvement</span>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-purple-700">
                      <span className="text-purple-500 mt-1">•</span>
                      <span>Understanding how users interact with our Service</span>
                    </li>
                    <li className="flex items-start gap-2 text-purple-700">
                      <span className="text-purple-500 mt-1">•</span>
                      <span>Feature usage statistics</span>
                    </li>
                    <li className="flex items-start gap-2 text-purple-700">
                      <span className="text-purple-500 mt-1">•</span>
                      <span>Performance monitoring and optimization</span>
                    </li>
                    <li className="flex items-start gap-2 text-purple-700">
                      <span className="text-purple-500 mt-1">•</span>
                      <span>User behavior analysis (anonymized)</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Third-Party Cookies */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Third-Party Cookies</h2>
              <p className="text-gray-700 mb-4">
                We may use third-party services that set their own cookies for:
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3 ml-16">
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-indigo-600" />
              <span className="text-gray-700">Payment processing</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-indigo-600" />
              <span className="text-gray-700">Analytics services</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-indigo-600" />
              <span className="text-gray-700">Customer support</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-indigo-600" />
              <span className="text-gray-700">Marketing tools</span>
            </div>
          </div>
        </div>

        {/* Data Protection */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-xl p-8 mb-6 border-2 border-indigo-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Data Protection</h2>
              <p className="text-gray-700 leading-relaxed">
                We comply with Indian data protection laws, including the Information 
                Technology Act, 2000. Cookie data is processed in accordance with our 
                Privacy Policy.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Card */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-2xl">
          <div className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Questions About Cookies?</h3>
            <p className="mb-6 text-white/90">
              Contact our privacy team for more information
            </p>
            <div className="space-y-2">
              <a
                href="mailto:privacy@ieeepaperpro.com"
                className="inline-block bg-white text-indigo-600 px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg"
              >
                support@ieeeassistant.in
              </a>
              <p className="text-sm text-white/80">
                Nagpur, Maharashtra, India
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400">
            © 2025 IEEE Paper Pro. All rights reserved. | Compliant with IT Act, 2000
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;